import { useCallback, useState } from 'react';
import type { IpcResult } from '@shared/types';

/**
 * Sprint 32 — Hook que envuelve una acción crítica para manejar
 * automáticamente el flow de step-up de 2FA.
 *
 * Uso:
 *   const stepUp = useStepUp();
 *   const ejecutar = () => stepUp.ejecutar(() => window.api.cierreDia.ejecutar(input));
 *   // En el JSX:
 *   <StepUpModal
 *     abierto={stepUp.modalAbierto}
 *     onConfirmado={stepUp.onConfirmado}
 *     onCancelar={stepUp.onCancelar}
 *   />
 *
 * Cómo funciona:
 *  - Llama a la acción. Si retorna `codigo === 'STEP_UP_REQUIRED'`, abre el modal.
 *  - Tras éxito en el modal, reintenta la acción una vez (con permiso step-up).
 *  - Si cancela, devuelve un resultado IpcResult cancelado.
 */

export interface StepUpControl<T> {
  modalAbierto: boolean;
  ejecutar: (accion: () => Promise<IpcResult<T>>) => Promise<IpcResult<T>>;
  onConfirmado: () => void;
  onCancelar: () => void;
}

export function useStepUp<T>(): StepUpControl<T> {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [pendingResolve, setPendingResolve] = useState<((r: IpcResult<T>) => void) | null>(null);
  const [pendingAccion, setPendingAccion] = useState<(() => Promise<IpcResult<T>>) | null>(null);

  const ejecutar = useCallback(async (accion: () => Promise<IpcResult<T>>): Promise<IpcResult<T>> => {
    const primerIntento = await accion();
    if (primerIntento.ok || primerIntento.codigo !== 'STEP_UP_REQUIRED') {
      return primerIntento;
    }

    // Necesita step-up. Abrir modal y devolver promesa que resuelve cuando
    // el modal se cierre (con éxito o cancelación).
    return new Promise<IpcResult<T>>((resolve) => {
      setPendingAccion(() => accion);
      setPendingResolve(() => resolve);
      setModalAbierto(true);
    });
  }, []);

  const onConfirmado = useCallback(async () => {
    setModalAbierto(false);
    if (pendingAccion && pendingResolve) {
      const segundoIntento = await pendingAccion();
      pendingResolve(segundoIntento);
    }
    setPendingAccion(null);
    setPendingResolve(null);
  }, [pendingAccion, pendingResolve]);

  const onCancelar = useCallback(() => {
    setModalAbierto(false);
    if (pendingResolve) {
      pendingResolve({
        ok: false,
        error: 'Acción cancelada',
        codigo: 'CANCELADO',
      });
    }
    setPendingAccion(null);
    setPendingResolve(null);
  }, [pendingResolve]);

  return { modalAbierto, ejecutar, onConfirmado, onCancelar };
}
